import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MailIcon from '@mui/icons-material/MailOutlineTwoTone';
import { useTranslation } from 'react-i18next';
import { setMessageCount } from '../../../../actions/message';
import useMessagePoll, { POLL_BACKGROUND_MS } from '../../../../hooks/useMessagePoll';

const RECENT_LIMIT = 6;

export default function Mail(props) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation();
    const message = useSelector(state => state.message);
    const navigate = useNavigate();
    const { location } = props;

    const [anchor, setAnchor] = useState(null);
    const [recent, setRecent] = useState([]);

    // The badge is kept current by the one shared interval, rather than the single fetch on mount
    // this button used to do — which went stale the moment a message arrived.
    const onUnread = useCallback((count) => {
        dispatch(setMessageCount(count));
    }, [dispatch]);

    useMessagePoll({ onUnread: onUnread, intervalMs: POLL_BACKGROUND_MS });

    const open = async (e) => {
        setAnchor(e.currentTarget);
        try {
            const res = await axios.get('/api/messagethread/list', { params: { size: RECENT_LIMIT } });
            setRecent(res.data.data || []);
        } catch {
            setRecent([]);
        }
    };

    const close = () => setAnchor(null);

    const go = (path) => {
        close();
        navigate(path);
    };

    return (
        <React.Fragment>
            <IconButton onClick={open} color="inherit" data-cy="mailButton">
                <Badge
                    overlap="rectangular"
                    badgeContent={message.messageCount}
                    color={location === 'bottom' ? 'primary' : 'secondary'}
                >
                    <MailIcon />
                </Badge>
            </IconButton>

            <Popover
                open={anchor != null}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 340, maxHeight: 420 } } }}
            >
                <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {t('messages')}
                    </Typography>
                </Box>
                <Divider />

                {recent.length === 0 && (
                    <Box sx={{ px: 2, py: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('noConversations', 'No conversations yet — start one below.')}
                        </Typography>
                    </Box>
                )}

                <List dense disablePadding>
                    {recent.map(thread => (
                        <ListItemButton
                            key={thread._id}
                            onClick={() => go('/messages/' + thread._id)}
                            sx={{ alignItems: 'flex-start' }}
                        >
                            <ListItemText
                                primary={
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        {thread.unread && (
                                            <Box sx={{
                                                width: 7, height: 7, borderRadius: '50%',
                                                bgcolor: 'error.main', flexShrink: 0
                                            }} />
                                        )}
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ fontWeight: thread.unread ? 700 : 500 }}
                                        >
                                            {thread.subject}
                                        </Typography>
                                    </Stack>
                                }
                                secondary={
                                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                                        {thread.lastMessageBy ? thread.lastMessageBy + ': ' : ''}
                                        {thread.lastMessagePreview}
                                    </Typography>
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>

                <Divider />
                <ListItemButton onClick={() => go('/messages')} data-cy="allMessages">
                    <ListItemText
                        primary={
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                {t('viewAllMessages', 'View all messages')}
                            </Typography>
                        }
                    />
                </ListItemButton>
            </Popover>
        </React.Fragment>
    );
}
